import { normalizeQuantity } from './quantity';

const normalizePositiveInteger = (value) => {
  const parsed = Number.parseInt(value, 10);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
};

const formatSlotMoney = (value) => `$${Number(value || 0).toLocaleString('de-DE')}`;

export const formatPosSlotQuantity = (value) => {
  const quantity = normalizeQuantity(value);
  const hasDecimals = Math.abs(quantity - Math.round(quantity)) > 0.0001;
  return quantity.toLocaleString('de-DE', hasDecimals
    ? { minimumFractionDigits: 0, maximumFractionDigits: 3 }
    : undefined);
};

export const buildPosSlotLayoutScopeKey = (branchId, cashRegisterId) => {
  const resolvedBranchId = normalizePositiveInteger(branchId);
  const resolvedCashRegisterId = normalizePositiveInteger(cashRegisterId);
  if (!resolvedBranchId || !resolvedCashRegisterId) return '';
  return `${resolvedBranchId}:${resolvedCashRegisterId}`;
};

const findProductForLegacySlot = (products, slot) => {
  const varietyId = normalizePositiveInteger(slot?.varietyId);
  const varietyPriceId = normalizePositiveInteger(slot?.varietyPriceId);
  if (!varietyId && !varietyPriceId) return null;

  for (const product of products || []) {
    for (const variety of product?.Varieties || []) {
      const matchesVariety = varietyId && Number(variety?.id) === Number(varietyId);
      const matchesPrice = varietyPriceId && (variety?.VarietyPrices || []).some(
        (price) => Number(price?.id) === Number(varietyPriceId)
      );
      if (matchesVariety || matchesPrice) return product;
    }
  }

  return null;
};

export const normalizePosSlotAssignment = (value, products = []) => {
  const productId = normalizePositiveInteger(value?.productId);
  if (productId) return { productId };

  const product = findProductForLegacySlot(products, value);
  if (product?.id) return { productId: product.id };

  const varietyId = normalizePositiveInteger(value?.varietyId);
  const varietyPriceId = normalizePositiveInteger(value?.varietyPriceId);
  if (varietyId && varietyPriceId) return { varietyId, varietyPriceId };
  return null;
};

const normalizeLayoutEntries = (entries, products = []) => {
  const seen = new Set();
  return (Array.isArray(entries) ? entries : [])
    .map((entry) => normalizePosSlotAssignment(entry, products))
    .filter(Boolean)
    .filter((entry) => {
      const key = entry.productId
        ? `product:${entry.productId}`
        : `legacy:${entry.varietyId}:${entry.varietyPriceId}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
};

export const normalizePosSlotLayouts = (value, products = []) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};

  const normalized = {};
  Object.entries(value).forEach(([scopeKey, entries]) => {
    const [branchIdRaw, cashRegisterIdRaw] = String(scopeKey || '').split(':');
    const normalizedScopeKey = buildPosSlotLayoutScopeKey(branchIdRaw, cashRegisterIdRaw);
    if (!normalizedScopeKey) return;
    normalized[normalizedScopeKey] = normalizeLayoutEntries(entries, products);
  });
  return normalized;
};

export const getPosSlotLayout = (layouts, branchId, cashRegisterId, products = []) => {
  const scopeKey = buildPosSlotLayoutScopeKey(branchId, cashRegisterId);
  if (!scopeKey) return [];
  const normalizedLayouts = normalizePosSlotLayouts(layouts, products);
  return Array.isArray(normalizedLayouts[scopeKey]) ? normalizedLayouts[scopeKey] : [];
};

export const setPosSlotLayout = (layouts, branchId, cashRegisterId, nextLayout, products = []) => {
  const scopeKey = buildPosSlotLayoutScopeKey(branchId, cashRegisterId);
  const normalizedLayouts = normalizePosSlotLayouts(layouts);
  if (!scopeKey) return normalizedLayouts;
  return {
    ...normalizedLayouts,
    [scopeKey]: normalizeLayoutEntries(nextLayout, products),
  };
};

export const removePosSlotAt = (layout, slotIndex) => (
  (Array.isArray(layout) ? layout : []).filter((_, index) => index !== slotIndex)
);

export const movePosSlot = (layout, fromIndex, toIndex) => {
  const entries = Array.isArray(layout) ? [...layout] : [];
  if (
    fromIndex === toIndex
    || fromIndex < 0
    || toIndex < 0
    || fromIndex >= entries.length
    || toIndex >= entries.length
  ) {
    return entries;
  }
  const [moved] = entries.splice(fromIndex, 1);
  entries.splice(toIndex, 0, moved);
  return entries;
};

export const findPosSlotDefinition = (products, slot) => {
  const normalizedSlot = normalizePosSlotAssignment(slot, products);
  if (!normalizedSlot) return null;

  if (normalizedSlot.productId) {
    const product = (products || []).find(
      (entry) => Number(entry?.id) === Number(normalizedSlot.productId)
    );
    return product ? { product, slot: normalizedSlot } : null;
  }

  const product = findProductForLegacySlot(products, normalizedSlot);
  return product ? { product, slot: normalizePosSlotAssignment(normalizedSlot, products) } : null;
};

const getProductPriceEntries = ({
  product,
  quantities = {},
  discounts = {},
  isPriceAllowedByRegister,
  resolveDisplayPrice,
}) => (
  (product?.Varieties || []).flatMap((variety) => (
    (variety?.VarietyPrices || []).map((price, priceIndex) => {
      const isSellable = price?.price !== null && price?.price !== undefined;
      const allowed = isSellable && (typeof isPriceAllowedByRegister === 'function'
        ? isPriceAllowedByRegister(price)
        : true);
      if (!allowed) return null;
      const unitDiscount = Number(discounts?.[variety?.id]?.[priceIndex] || 0);
      const display = typeof resolveDisplayPrice === 'function'
        ? resolveDisplayPrice(variety, price, unitDiscount)
        : null;
      const displayPrice = Number.isFinite(Number(display?.price))
        ? Number(display.price)
        : Number(price?.price || 0);
      return {
        variety,
        price,
        priceIndex,
        displayPrice,
        quantity: normalizeQuantity(quantities?.[variety?.id]?.[priceIndex] || 0),
      };
    })
  ))
).filter(Boolean);

const buildPriceRangeLabel = (entries) => {
  const prices = entries
    .map((entry) => Number(entry.displayPrice))
    .filter((value) => Number.isFinite(value));
  if (!prices.length) return '--';
  const minPrice = Math.min(...prices);
  const maxPrice = Math.max(...prices);
  if (minPrice === maxPrice) return formatSlotMoney(minPrice);
  return `${formatSlotMoney(minPrice)} - ${formatSlotMoney(maxPrice)}`;
};

export const buildPosSlotPickerEntries = ({
  products = [],
  isPriceAllowedByRegister,
  resolveDisplayPrice,
}) => (
  (products || [])
    .map((product) => {
      const priceEntries = getProductPriceEntries({
        product,
        isPriceAllowedByRegister,
        resolveDisplayPrice,
      });
      if (!priceEntries.length) return null;
      const varietyCount = new Set(priceEntries.map((entry) => entry.variety?.id)).size;
      const soldOut = priceEntries.every((entry) => entry.price?.isSoldOut);
      return {
        key: `product:${product?.id}`,
        product,
        productId: product?.id,
        productName: product?.productName || 'Producto',
        detailLabel: `${varietyCount} ${varietyCount === 1 ? 'variedad' : 'variedades'}`,
        priceLabel: buildPriceRangeLabel(priceEntries),
        imageSrc: product?.productImage || priceEntries[0]?.variety?.varietyImage || 'https://placehold.co/200',
        soldOut,
      };
    })
    .filter(Boolean)
    .sort((left, right) => left.productName.localeCompare(right.productName))
);

export const buildResolvedPosSlots = ({
  products = [],
  slotLayout = [],
  quantities = {},
  discounts = {},
  isPriceAllowedByRegister,
  resolveDisplayPrice,
}) => (
  (slotLayout || []).map((slot, slotIndex) => {
    const resolved = findPosSlotDefinition(products, slot);
    if (!resolved) {
      return {
        key: `missing:${slotIndex}`,
        slotIndex,
        slot,
        disabled: true,
        invalid: true,
        invalidReason: 'El producto ya no existe.',
        productName: 'Producto eliminado',
        detailLabel: 'No disponible',
        priceLabel: '--',
        quantityLabel: '0',
        currentQuantity: 0,
      };
    }

    const { product } = resolved;
    const priceEntries = getProductPriceEntries({
      product,
      quantities,
      discounts,
      isPriceAllowedByRegister,
      resolveDisplayPrice,
    });
    const currentQuantity = priceEntries.reduce(
      (sum, entry) => normalizeQuantity(sum + entry.quantity),
      0
    );
    const varietyCount = new Set(priceEntries.map((entry) => entry.variety?.id)).size;
    const disabled = !priceEntries.length || priceEntries.every((entry) => entry.price?.isSoldOut);
    let invalidReason = '';
    if (!priceEntries.length) {
      invalidReason = 'Este producto no tiene precios permitidos para la caja actual.';
    } else if (priceEntries.every((entry) => entry.price?.isSoldOut)) {
      invalidReason = 'Producto agotado.';
    }

    return {
      key: `product:${product?.id || 'missing'}:${slotIndex}`,
      slotIndex,
      slot: normalizePosSlotAssignment(slot, products),
      product,
      productId: product?.id,
      productName: product?.productName || 'Producto',
      detailLabel: priceEntries.length
        ? `${varietyCount} ${varietyCount === 1 ? 'variedad' : 'variedades'}`
        : 'Sin precios disponibles',
      priceLabel: buildPriceRangeLabel(priceEntries),
      imageSrc: product?.productImage || priceEntries[0]?.variety?.varietyImage || 'https://placehold.co/200',
      currentQuantity,
      quantityLabel: formatPosSlotQuantity(currentQuantity),
      disabled,
      invalid: !priceEntries.length,
      invalidReason,
      soldOut: priceEntries.length > 0 && priceEntries.every((entry) => entry.price?.isSoldOut),
    };
  })
);
